import { z } from 'zod'
import { LEAD_NAME_MAX_LENGTH } from '@nwwp/database'

/**
 * Investable assets: the canonical five buckets. These values are the
 * `investable_assets_range` enum written straight through to Salesforce
 * `Investable_Assets_Range__c` — do not redraw them to suit a form design.
 */
export const ASSET_RANGE_VALUES = [
  'UNDER_100K',
  'FROM_100_TO_250K',
  'FROM_250_TO_500K',
  'FROM_500K_TO_1M',
  'OVER_1M',
] as const

export const ASSET_RANGE_OPTIONS: {
  value: (typeof ASSET_RANGE_VALUES)[number]
  label: string
}[] = [
  { value: 'UNDER_100K', label: 'Under $100,000' },
  { value: 'FROM_100_TO_250K', label: '$100,000 – $250,000' },
  { value: 'FROM_250_TO_500K', label: '$250,000 – $500,000' },
  { value: 'FROM_500K_TO_1M', label: '$500,000 – $1,000,000' },
  { value: 'OVER_1M', label: 'Over $1,000,000' },
]

/**
 * Household income: deliberately a different set of ranges from the assets
 * above, drawn to match the LeadScorer's income buckets one-for-one
 * (`incomeBucket` in `@nwwp/lead-scorer`: <50k / 50–100k / 100–200k / 200–500k /
 * 500k–1M / >1M). Because each range sits entirely inside one scorer bucket, the
 * representative `annualIncome` below cannot change which bucket a lead scores
 * into — only its position within that bucket. Redrawing these ranges without
 * moving the scorer's boundaries reintroduces that ambiguity.
 */
export const INCOME_RANGE_VALUES = [
  'UNDER_50K',
  'FROM_50_TO_100K',
  'FROM_100_TO_200K',
  'FROM_200_TO_500K',
  'FROM_500K_TO_1M',
  'OVER_1M',
] as const

/**
 * `annualIncome` is the representative value written to the Lead's numeric
 * `annual_income` (Salesforce `Annual_Income__c`). Range midpoints, except
 * `OVER_1M`, which has no upper bound to average — 1.5M is a stand-in that sits
 * safely inside `5_gt1M` and well under the scorer's `> 5M` suspicious-income
 * threshold.
 */
export const INCOME_RANGE_OPTIONS: {
  value: (typeof INCOME_RANGE_VALUES)[number]
  label: string
  annualIncome: number
}[] = [
  { value: 'UNDER_50K', label: 'Under $50,000', annualIncome: 25_000 },
  { value: 'FROM_50_TO_100K', label: '$50,000 – $100,000', annualIncome: 75_000 },
  { value: 'FROM_100_TO_200K', label: '$100,000 – $200,000', annualIncome: 150_000 },
  { value: 'FROM_200_TO_500K', label: '$200,000 – $500,000', annualIncome: 350_000 },
  { value: 'FROM_500K_TO_1M', label: '$500,000 – $1,000,000', annualIncome: 750_000 },
  { value: 'OVER_1M', label: 'Over $1,000,000', annualIncome: 1_500_000 },
]

export const incomeToAnnualIncome = (range: string) =>
  INCOME_RANGE_OPTIONS.find((o) => o.value === range)?.annualIncome ?? null

// No z.preprocess here — the selects always hold a string, and preprocess would
// widen the field's input type to unknown and leak casts into the form.
const selectField = (message: string, values: readonly [string, ...string[]]) =>
  z.string().min(1, message).pipe(z.enum(values))

export const WebinarRegistrationSchema = z.object({
  /**
   * The design captures a single "Name" field. Split on the first space so the
   * Lead still gets the first_name / last_name it requires.
   */
  name: z
    .string()
    .trim()
    .min(1, 'Please enter your name.')
    .max(LEAD_NAME_MAX_LENGTH.firstName + LEAD_NAME_MAX_LENGTH.lastName, 'Please enter a shorter name.')
    .refine((v) => v.includes(' '), 'Please enter your first and last name.'),
  household_income_range: selectField('Please select your household income.', [...INCOME_RANGE_VALUES]),
  investable_assets_range: selectField('Please select your investable assets.', [...ASSET_RANGE_VALUES]),
  email: z.email('Please enter a valid email address.'),
  phone: z.string().regex(/^\d{10}$/, 'Please enter your phone number.'),
})

export type WebinarRegistration = z.infer<typeof WebinarRegistrationSchema>

/** Splits the single Name field on the first space. */
export const splitName = (name: string) => {
  const [first, ...rest] = name.trim().split(/\s+/)
  return {
    first_name: (first ?? '').slice(0, LEAD_NAME_MAX_LENGTH.firstName),
    last_name: rest.join(' ').slice(0, LEAD_NAME_MAX_LENGTH.lastName),
  }
}
